import type { WebsiteBlock } from '../../types'
import { useBranding } from '../../contexts/BrandingContext'
import { useI18n } from '../../i18n'
import { Check } from 'lucide-react'

interface PricingProps {
  title: string
  titleEn?: string
  plans: Array<{
    name: string
    nameEn?: string
    price: string
    period?: string
    features: string[]
    highlighted?: boolean
    ctaText?: string
    ctaTextEn?: string
  }>
}

export default function PricingBlock({ block }: { block: WebsiteBlock }) {
  const { branding } = useBranding()
  const { lang } = useI18n()
  const p = block.props as PricingProps

  const title = lang === 'en' && p.titleEn ? p.titleEn : p.title
  const hasContent = title?.trim() || (p.plans && p.plans.length > 0)
  if (!hasContent) return null

  return (
    <section className="py-20 px-6 bg-gray-50/50">
      <div className="max-w-5xl mx-auto">
        {title?.trim() && (
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 tracking-tight" style={{ color: branding.secondaryColor }}>
            {title}
          </h2>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(p.plans || []).map((plan, i) => (
            <div key={i} className={`rounded-2xl p-6 border transition-all ${
              plan.highlighted
                ? 'bg-white shadow-lg scale-105 border-transparent'
                : 'bg-white border-black/[0.04]'
            }`}>
              {plan.highlighted && (
                <span className="inline-block px-2 py-0.5 text-[10px] font-medium text-white rounded-full mb-3"
                  style={{ backgroundColor: branding.primaryColor }}>推荐</span>
              )}
              <h3 className="text-base font-semibold mb-2" style={{ color: branding.secondaryColor }}>
                {lang === 'en' && plan.nameEn ? plan.nameEn : plan.name}
              </h3>
              <div className="mb-4">
                <span className="text-3xl font-bold" style={{ color: branding.secondaryColor }}>{plan.price}</span>
                {plan.period && <span className="text-xs text-gray-400 ml-1">{plan.period}</span>}
              </div>
              <ul className="space-y-2 mb-6">
                {(plan.features || []).map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-xs text-gray-500">
                    <Check className="h-3.5 w-3.5 shrink-0" style={{ color: branding.primaryColor }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button className="w-full py-2.5 rounded-xl text-sm font-medium transition-all"
                style={plan.highlighted
                  ? { backgroundColor: branding.primaryColor, color: '#fff' }
                  : { color: branding.secondaryColor, border: `1px solid ${branding.secondaryColor}20` }
                }>
                {lang === 'en' && plan.ctaTextEn ? plan.ctaTextEn : (plan.ctaText || '选择')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
